import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, FormControlLabel, Paper, Stack, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import PostAddIcon from "@mui/icons-material/PostAdd";
import SaveIcon from "@mui/icons-material/Save";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { stockTakesApi, itemBinsApi, type StockTakeLineDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { StatusPill } from "@/components/StatusPill";
import { DetailSkeleton } from "@/components/Skeletons";
import { formatDate } from "@/shared/datetime";


interface LineDraft { countedQty: number; lineNotes: string }

/**
 * UC-M1-INV-08 — Stock take detail. While Draft, operators edit CountedQty per line.
 * Posting writes one ADJUSTMENT (positive delta) or WRITE_OFF (negative delta)
 * ledger row per non-zero variance and locks the document.
 */
export function StockTakeDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canPost = useAuth(s => s.hasPermission("inventory.stocktake.post"));
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [postOpen, setPostOpen] = useState(false);

  const query = useQuery({
    queryKey: ["stock-take", id],
    queryFn: () => stockTakesApi.get(id),
    enabled: !!id,
  });

  // Seed local draft state when data arrives or refreshes.
  useEffect(() => {
    if (query.data) {
      setDrafts(Object.fromEntries(query.data.lines.map(l => [
        l.id, { countedQty: l.countedQty, lineNotes: l.lineNotes ?? "" },
      ])));
    }
  }, [query.data]);

  // Every bin in the warehouse, once. Counting is a physical walk, so the sheet is far easier to
  // work through in bin order than in whatever order the lines were generated — and one fetch
  // beats a lookup per line.
  const [byBin, setByBin] = useState(false);
  const warehouseId = query.data?.warehouseId;
  const binsQuery = useQuery({
    queryKey: ["item-bins", warehouseId],
    queryFn: () => itemBinsApi.list({ warehouseId: warehouseId!, pageSize: 500 }),
    enabled: !!warehouseId,
    staleTime: 5 * 60_000,
  });
  const binByItem = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of binsQuery.data?.items ?? []) {
      // Primary wins; otherwise first seen, so a part-configured item still sorts somewhere sane.
      if (b.isPrimary || !m.has(b.itemId)) m.set(b.itemId, b.locationCode);
    }
    return m;
  }, [binsQuery.data]);

  const orderedLines = useMemo(() => {
    const lines = query.data?.lines ?? [];
    if (!byBin) return lines;
    return [...lines].sort((a, b) => {
      const ab = binByItem.get(a.itemId) ?? "", bb = binByItem.get(b.itemId) ?? "";
      // Unbinned items last — they need looking up and would otherwise break the walk.
      if (!ab) return bb ? 1 : 0;
      if (!bb) return -1;
      return ab.localeCompare(bb, undefined, { numeric: true, sensitivity: "base" });
    });
  }, [query.data, byBin, binByItem]);

  const saveCounts = useMutation({
    mutationFn: () => stockTakesApi.updateCounts(id, Object.entries(drafts).map(([lineId, d]) => ({
      lineId, countedQty: d.countedQty, lineNotes: d.lineNotes || undefined,
    }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stock-take", id] }),
  });

  const postMutation = useMutation({
    mutationFn: () => stockTakesApi.post(id, query.data!.rowVersion),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stock-take", id] });
      qc.invalidateQueries({ queryKey: ["stock-takes"] });
      setPostOpen(false);
    },
  });

  if (query.isLoading || !query.data) return <DetailSkeleton />;
  const st = query.data;
  const isDraft = st.status === "Draft";
  const variancePreview = (l: StockTakeLineDto) =>
    (drafts[l.id]?.countedQty ?? l.countedQty) - l.systemQty;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>{st.documentNo}</Typography>
          <StatusPill label={st.status} />
        </Stack>
        {isDraft && (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<SaveIcon />} disabled={saveCounts.isPending}
              onClick={() => saveCounts.mutate()}>Save counts</Button>
            {canPost && (
              <Button variant="contained" color="success" startIcon={<PostAddIcon />}
                onClick={() => setPostOpen(true)}>Post variance</Button>
            )}
          </Stack>
        )}
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Stack><Typography variant="caption" color="text.secondary">Warehouse</Typography>
            <Typography>{st.warehouseName}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Date</Typography>
            <Typography>{formatDate(st.documentDate)}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Lines</Typography>
            <Typography>{st.lineCount}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">|Δ| total</Typography>
            <Typography>{st.totalVariance}</Typography></Stack>
        </Stack>
        {st.notes && (
          <Stack sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Notes</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{st.notes}</Typography>
          </Stack>
        )}
        {saveCounts.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(saveCounts.error)}</Alert>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">Count lines</Typography>
          {binByItem.size > 0 && (
            <FormControlLabel
              control={<Switch size="small" checked={byBin} onChange={(_, v) => setByBin(v)} />}
              label={<Typography variant="body2">Walk order (by bin)</Typography>}
            />
          )}
        </Stack>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Bin</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell align="right">System</TableCell>
            <TableCell align="right" sx={{ width: 140 }}>Counted</TableCell>
            <TableCell align="right">Variance</TableCell>
            <TableCell>Line notes</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {orderedLines.map(l => {
              const v = variancePreview(l);
              return (
                <TableRow key={l.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{l.itemName}</Typography>
                    <Typography variant="caption" color="text.secondary">{l.sku}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {binByItem.get(l.itemId) ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>{l.batchNo ?? "—"}</TableCell>
                  <TableCell align="right">{l.systemQty}</TableCell>
                  <TableCell align="right">
                    {isDraft
                      ? <TextField type="number" size="small" sx={{ width: 120 }}
                          value={drafts[l.id]?.countedQty ?? 0}
                          onChange={(e) => setDrafts(prev => ({
                            ...prev,
                            [l.id]: { ...prev[l.id], countedQty: Number(e.target.value) || 0 },
                          }))}
                          inputProps={{ min: 0, step: "0.001", style: { textAlign: "right" } }} />
                      : <Typography>{l.countedQty}</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 600,
                    color: v < 0 ? "error.main" : v > 0 ? "success.main" : "text.secondary",
                  }}>
                    {v > 0 ? "+" : ""}{v}
                  </TableCell>
                  <TableCell>
                    {isDraft
                      ? <TextField size="small" fullWidth value={drafts[l.id]?.lineNotes ?? ""}
                          onChange={(e) => setDrafts(prev => ({
                            ...prev,
                            [l.id]: { ...prev[l.id], lineNotes: e.target.value },
                          }))} />
                      : <Typography variant="body2">{l.lineNotes ?? "—"}</Typography>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Button onClick={() => navigate("/inventory/stocktakes")}>Back to list</Button>

      <Dialog open={postOpen} onClose={() => setPostOpen(false)} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => setPostOpen(false)}>Post stock-take {st.documentNo}?</ClosableDialogTitle>
        <DialogContent>
          <DialogContentText>
            Posting writes one immutable ledger row per non-zero variance — positive deltas
            as ADJUSTMENT, negative deltas as WRITE_OFF. Make sure you've saved the latest
            counts before posting; corrections after Post need a new stock adjustment.
          </DialogContentText>
          {postMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(postMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={postMutation.isPending}
            startIcon={postMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
            onClick={() => postMutation.mutate()}>Post variance</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
